import { NgFor, NgIf } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ApiService } from '../../core/api.service';
import { LocalDatePipe, LocalDateTimePipe } from '../../core/date-utils';
import { Contagem, ContagemAnexo } from '../../core/models';

@Component({
  standalone: true,
  imports: [NgIf, NgFor, RouterLink, LocalDatePipe, LocalDateTimePipe],
  template: `
    <header class="page-header" *ngIf="contagem">
      <div>
        <h2>Contagem {{ contagem.id_contagem }}</h2>
        <p>{{ contagem.cliente }} - {{ contagem.status }}</p>
      </div>
      <div class="header-actions">
        <a *ngIf="contagem.status === 'ABERTA'" class="primary-link" [routerLink]="['/contagens', contagem.id_contagem, 'editar']">Editar</a>
        <button type="button" (click)="reabrir()" *ngIf="contagem.status === 'FINALIZADA'">Reabrir</button>
        <button type="button" (click)="romaneio()">Gerar romaneio</button>
      </div>
    </header>

    <p class="error" *ngIf="erro">{{ erro }}</p>
    <p class="success" *ngIf="mensagem">{{ mensagem }}</p>

    <section class="panel view" *ngIf="contagem">
      <dl>
        <div><dt>Cliente</dt><dd>{{ contagem.cliente }}</dd></div>
        <div><dt>Produto</dt><dd>{{ contagem.produtos_resumo || contagem.codigo_produto + ' - ' + contagem.produto }}</dd></div>
        <div><dt>Data</dt><dd>{{ contagem.data_contagem | localDate }}</dd></div>
        <div><dt>Referência</dt><dd>{{ contagem.referencia }}</dd></div>
        <div><dt>Descrição</dt><dd>{{ contagem.descricao }}</dd></div>
        <div><dt>Cor</dt><dd>{{ contagem.cor }}</dd></div>
        <div><dt>Responsáveis</dt><dd>{{ contagem.responsaveis || contagem.responsavel }}</dd></div>
      </dl>

      <table class="size-table">
        <thead><tr><th>Produto</th><th>Tamanho</th><th>Quantidade</th><th>Defeitos</th><th>Total</th></tr></thead>
        <tbody>
          <tr *ngFor="let item of contagem.itens">
            <td data-label="Produto">{{ item.codigo_produto }} - {{ item.produto }}</td>
            <td data-label="Tamanho">{{ item.tamanho }}</td>
            <td data-label="Quantidade">{{ item.quantidade }}</td>
            <td data-label="Defeitos">{{ item.defeitos || 0 }}</td>
            <td data-label="Total">{{ totalItem(item) }}</td>
          </tr>
        </tbody>
      </table>

      <div class="total-line">Total: <strong>{{ contagem.total_pecas }}</strong> peças</div>
      <p><strong>Observação:</strong> {{ contagem.observacao }}</p>

      <figure class="draft-photo-preview view-photo" *ngIf="rascunhoPreviewUrl">
        <button class="photo-zoom-trigger" type="button" (click)="abrirImagem()" aria-label="Ampliar foto do rascunho">
          <img [src]="rascunhoPreviewUrl" alt="Foto do rascunho da contagem">
        </button>
        <figcaption>{{ rascunho?.nome_original || 'rascunho-contagem.jpg' }}</figcaption>
      </figure>
    </section>

    <div class="modal-backdrop image-lightbox-backdrop" *ngIf="imagemAmpliada" (click)="fecharImagem()">
      <section class="image-lightbox" role="dialog" aria-modal="true" aria-labelledby="imagem-contagem-titulo" (click)="$event.stopPropagation()">
        <header class="image-lightbox-header">
          <h3 id="imagem-contagem-titulo">{{ rascunho?.nome_original || 'Foto do rascunho da contagem' }}</h3>
          <button class="ghost" type="button" (click)="fecharImagem()" aria-label="Fechar imagem ampliada">Fechar</button>
        </header>
        <img [src]="rascunhoPreviewUrl" alt="Foto ampliada do rascunho da contagem">
      </section>
    </div>

    <section class="panel" *ngIf="contagem?.logs?.length">
      <h3>Histórico de alterações</h3>
      <table class="data-table">
        <thead><tr><th>Data</th><th>Ação</th><th>Usuário</th><th>Status anterior</th><th>Status novo</th><th>Observação</th></tr></thead>
        <tbody>
          <tr *ngFor="let log of contagem?.logs">
            <td data-label="Data">{{ log.criado_em | localDateTime }}</td>
            <td data-label="Ação">{{ log.acao }}</td>
            <td data-label="Usuário">{{ log.usuario || '-' }}</td>
            <td data-label="Status anterior">{{ log.status_anterior || '-' }}</td>
            <td data-label="Status novo">{{ log.status_novo || '-' }}</td>
            <td data-label="Observação">{{ log.observacao || '-' }}</td>
          </tr>
        </tbody>
      </table>
    </section>
  `
})
export class ContagemVisualizarComponent implements OnInit, OnDestroy {
  private api = inject(ApiService);
  private route = inject(ActivatedRoute);
  contagem?: Contagem;
  erro = '';
  mensagem = '';
  rascunho?: ContagemAnexo;
  rascunhoPreviewUrl = '';
  imagemAmpliada = false;
  private rascunhoObjectUrl = '';

  ngOnInit() {
    this.carregar();
  }

  ngOnDestroy() {
    this.liberarPreviewTemporario();
  }

  @HostListener('document:keydown.escape')
  fecharImagem() {
    this.imagemAmpliada = false;
  }

  abrirImagem() {
    this.imagemAmpliada = true;
  }

  carregar() {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    this.api.contagem(id).subscribe((contagem) => {
      this.contagem = contagem;
      this.carregarRascunho(contagem);
    });
  }

  reabrir() {
    if (!this.contagem) return;
    const confirmar = window.confirm(`Reabrir a contagem ${this.contagem.id_contagem} para edição?`);
    if (!confirmar) return;

    this.erro = '';
    this.mensagem = '';
    this.api.reabrirContagem(this.contagem.id_contagem).subscribe({
      next: () => {
        this.mensagem = 'Contagem reaberta para edição';
        this.carregar();
      },
      error: (error) => (this.erro = error.error?.message || 'Erro ao reabrir contagem')
    });
  }

  romaneio() {
    if (this.contagem) this.api.abrirRomaneio(this.contagem.id_contagem);
  }

  totalItem(item: { quantidade?: number; defeitos?: number; total?: number }) {
    return Number(item.total || 0) || Number(item.quantidade || 0) + Number(item.defeitos || 0);
  }

  private carregarRascunho(contagem: Contagem) {
    this.rascunho = contagem.anexos?.find((anexo) => anexo.tipo === 'RASCUNHO');
    if (!this.rascunho) {
      this.definirPreviewRascunho('');
      return;
    }

    this.api.anexoContagem(contagem.id_contagem, this.rascunho.id_anexo).subscribe({
      next: (blob) => {
        if (!this.rascunho || this.rascunho.id_contagem !== contagem.id_contagem) return;
        const url = URL.createObjectURL(blob);
        this.definirPreviewRascunho(url, true);
      },
      error: () => this.definirPreviewRascunho('')
    });
  }

  private definirPreviewRascunho(url: string, objectUrl = false) {
    this.liberarPreviewTemporario();
    this.rascunhoPreviewUrl = url;
    this.rascunhoObjectUrl = objectUrl ? url : '';
  }

  private liberarPreviewTemporario() {
    if (!this.rascunhoObjectUrl) return;
    URL.revokeObjectURL(this.rascunhoObjectUrl);
    this.rascunhoObjectUrl = '';
  }
}
